import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import instance from "@/lib/axios";
import { queryClient } from "@/lib/query-client";
import type {
  MyPermissions,
  Permission,
  Role,
  RolePayload,
} from "@/types/authorization";
import { userKeys } from "@/hooks/api/user/keys";
import { useFeatureStore } from "@/stores/useFeatureStore";

import { authorizationKeys } from "./keys";

function url(path: string): string {
  return `/api/authorization/${path}`;
}

async function fetchMyPermissions(): Promise<MyPermissions> {
  const response = await instance.get<MyPermissions>(url("me/permissions/"));
  return response.data;
}

/**
 * What the current user may do.
 *
 * Fetched once per session alongside the profile. No permission data rides on
 * the JWT, so a role change applies on the very next request with no re-login —
 * which is also why this must be invalidated whenever an admin edits roles
 * rather than cached indefinitely.
 */
export function useMyPermissions(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: authorizationKeys.myPermissions,
    queryFn: fetchMyPermissions,
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    meta: { errorMessage: "Couldn't load your permissions." },
  });
}

/**
 * The same capability set, outside React — for router `beforeLoad` guards,
 * which cannot call a hook. Shares the hook's cache entry, so a guard and the
 * components behind it never disagree.
 */
export async function ensureMyPermissions(): Promise<MyPermissions | undefined> {
  try {
    return await queryClient.ensureQueryData({
      queryKey: authorizationKeys.myPermissions,
      queryFn: fetchMyPermissions,
      staleTime: 5 * 60 * 1000,
    });
  } catch {
    return undefined;
  }
}

async function fetchRoles(): Promise<Role[]> {
  const response = await instance.get(url("roles/"));
  return response.data.results ?? response.data;
}

export function useRoles(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: authorizationKeys.roles,
    queryFn: fetchRoles,
    enabled: options?.enabled ?? true,
    meta: { errorMessage: "Couldn't load roles." },
  });
}

async function fetchPermissionCatalogue(module?: string): Promise<Permission[]> {
  const response = await instance.get(url("permissions/"), {
    params: module ? { module } : undefined,
  });
  return response.data.results ?? response.data;
}

/** The catalogue, for building the permission matrix. Grouped by module. */
export function usePermissionCatalogue(module?: string) {
  return useQuery({
    queryKey: authorizationKeys.permissions(module),
    queryFn: () => fetchPermissionCatalogue(module),
    staleTime: 30 * 60 * 1000, // the catalogue is code; it changes on deploy
    meta: { errorMessage: "Couldn't load the permission catalogue." },
  });
}

/** Anything that changes a role can change what the current user may do. */
function useAuthorizationInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: authorizationKeys.all });
    // The feature list folds in the caller's role blocks.
    void useFeatureStore.getState().invalidate();
  };
}

export function useCreateRole() {
  const invalidate = useAuthorizationInvalidation();
  return useMutation({
    mutationFn: async (payload: RolePayload) =>
      (await instance.post<Role>(url("roles/"), payload)).data,
    onSuccess: invalidate,
    meta: { errorMessage: "Couldn't create the role." },
  });
}

export function useUpdateRole() {
  const invalidate = useAuthorizationInvalidation();
  return useMutation({
    mutationFn: async ({ id, ...payload }: RolePayload & { id: number }) =>
      (await instance.patch<Role>(url(`roles/${id}/`), payload)).data,
    onSuccess: invalidate,
    meta: { errorMessage: "Couldn't update the role." },
  });
}

export function useDeleteRole() {
  const invalidate = useAuthorizationInvalidation();
  return useMutation({
    mutationFn: async (id: number) => {
      await instance.delete(url(`roles/${id}/`));
    },
    onSuccess: invalidate,
    meta: { errorMessage: "Couldn't delete the role." },
  });
}

export function useDuplicateRole() {
  const invalidate = useAuthorizationInvalidation();
  return useMutation({
    mutationFn: async ({ id, name }: { id: number; name: string }) =>
      (await instance.post<Role>(url(`roles/${id}/duplicate/`), { name })).data,
    onSuccess: invalidate,
    meta: { errorMessage: "Couldn't duplicate the role." },
  });
}

export function useAssignRole() {
  const invalidate = useAuthorizationInvalidation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ roleId, userId }: { roleId: number; userId: number }) => {
      await instance.post(url(`roles/${roleId}/assign/`), { user: userId });
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
    meta: { errorMessage: "Couldn't assign the role." },
  });
}
